import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Flex, Input, notification, Spin, Tooltip } from "antd";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { getRouteFindAllByMarketId } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { MarketNode, Route } from "tm-shared/types";
import { countText } from "tm-shared/utils";

import { MarketCardBase } from "@/components/MarketCardBase";
import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { PrimaryText } from "@/components/Typography/Styled";

import { RELATION_TOOLTIPS } from "../DuplicateByAddressModal.constants";
import type { MarketRelationsFormValues } from "../DuplicateByAddressModal.types";

interface MarketRelationRowProps {
    market: MarketNode;
    level?: number;
    formValues: MarketRelationsFormValues;
    hasParentSelected: boolean;
    setValue: (name: string, value: string | null) => void;
}

export const MarketRelationRow: FC<MarketRelationRowProps> = ({
    market,
    level = 0,
    formValues,
    hasParentSelected,
    setValue,
}) => {
    const fieldName = market.id;
    const currentValue = formValues[fieldName];

    const disableParentOption = hasParentSelected && currentValue !== "parent";
    const [routeCountLoading, setRouteCountLoading] = useState(false);
    const [routes, setRoutes] = useState<Route[]>([]);

    const routesLoaded = useRef<boolean>(false);

    const options = useMemo(
        () => [
            {
                label: "Батьківська ТТ",
                value: "parent",
                disabled: disableParentOption,
            },
            { label: "Незалежна ТТ", value: "independent" },
            { label: "Архівна ТТ", value: "archived" },
        ],
        [disableParentOption],
    );

    const isParent = !!market.child;

    const isArchiveSelectedField = currentValue === "archived";

    const hasRoutes = routes.length > 0;

    useEffect(() => {
        if (
            routesLoaded.current ||
            routeCountLoading ||
            !isArchiveSelectedField
        ) {
            return;
        }

        const fetchRouteCount = async () => {
            try {
                setRouteCountLoading(true);
                const response = await getRouteFindAllByMarketId({
                    marketId: market.id,
                });

                setRoutes(response.result);
            } catch (error) {
                notification.error({
                    message: "Не вдалося отримати інформації про маршрути",
                });
            } finally {
                setRouteCountLoading(false);
                routesLoaded.current = true;
            }
        };

        fetchRouteCount();
    }, [isArchiveSelectedField, routeCountLoading, market.id]);

    let status;

    if (market.isArchived) {
        status = "archived" as const;
    } else if (hasRoutes && isArchiveSelectedField) {
        status = "hasRoutes" as const;
    }

    const relationKey =
        currentValue ??
        (isParent ? "parent" : market.isArchived ? "archived" : "");

    const tooltipText =
        RELATION_TOOLTIPS[relationKey] ?? "Спочатку оберіть відношення";

    return (
        <>
            <MarketCardBase
                market={market}
                isChild={level > 0}
                status={status}
                bottomSlot={
                    <Flex vertical gap={16}>
                        <Flex align="center" gap={10}>
                            <Tooltip title={tooltipText}>
                                <div>
                                    <InfoCircleOutlined
                                        style={{ fontSize: 18 }}
                                    />
                                </div>
                            </Tooltip>

                            {market.isArchived || isParent ? (
                                <>
                                    <Input
                                        disabled
                                        size="large"
                                        value={
                                            isParent
                                                ? "Батьківська ТТ"
                                                : "Архівна ТТ"
                                        }
                                    />
                                </>
                            ) : (
                                <RHFAutocomplete
                                    name={fieldName}
                                    allowClear={false}
                                    placeholder="Оберіть відношення"
                                    options={options}
                                    getOptionDisabled={(option) =>
                                        !!option.disabled
                                    }
                                    rules={{
                                        required: validationMessage.required,
                                    }}
                                    selectProps={{
                                        size: "large",
                                    }}
                                    style={{ width: "100%" }}
                                />
                            )}
                        </Flex>

                        {routeCountLoading ? (
                            <Flex justify={"center"} align={"center"}>
                                <Spin
                                    indicator={<LoadingOutlined spin />}
                                    size="large"
                                />
                            </Flex>
                        ) : (
                            hasRoutes &&
                            isArchiveSelectedField && (
                                <Flex vertical gap={4}>
                                    <PrimaryText $textColor="error">
                                        Ця ТТ знаходиться у{" "}
                                        {countText.route(routes.length)}
                                    </PrimaryText>

                                    <Flex>
                                        {routes.map(({ name, uuid }) => (
                                            <PrimaryText
                                                key={uuid}
                                                $textColor="disabled"
                                            >
                                                - {name}
                                            </PrimaryText>
                                        ))}
                                    </Flex>
                                </Flex>
                            )
                        )}
                    </Flex>
                }
            />

            {market.child && (
                <MarketRelationRow
                    market={market.child}
                    level={level + 1}
                    formValues={formValues}
                    hasParentSelected={hasParentSelected}
                    setValue={setValue}
                />
            )}
        </>
    );
};
